import { Flex, Text, Box, type BoxProps } from '@chakra-ui/react'
import { useRecoilValue } from 'recoil'

import { circlesBreakpointState } from '../../state'
import { getBreakpointValue } from '../../utils'

interface CandleProps {
	transform?: string
}

type CombinedProps = CandleProps & BoxProps

export const Candle = ({ transform = undefined, ...rest }: CombinedProps) => {
	return (
		<Box
			className="holder"
			w="10%"
			h="100%"
			pos="absolute"
			bottom={0}
			left={0}
			right={0}
			m="auto"
			zIndex={1334}
			transform={transform}
			transformOrigin="bottom center"
			{...rest}
		>
			<Box
				className="candle"
				pos="absolute"
				bottom="0"
				w="100%"
				h="75%"
				borderRadius="150px / 40px"
				boxShadow="inset 20px -30px 50px 0 rgba(0, 0, 0, 0.4), inset -20px 0 50px 0 rgba(0, 0, 0, 0.4)"
				bgGradient="linear(to-b, #e48825, #e78e0e, #833c03, #4c1a03 50%, #1c0900)"
				_before={{
					pos: 'absolute',
					content: '""',
					width: '100%',
					height: '10%',
					borderRadius: '50%',
					border: '2px solid #d47401',
					background: 'radial-gradient(#ffef80, #b86409 60%) radial-gradient(#eaa121, #8e4901 45%, #b86409 80%)',
				}}
				_after={{
					pos: 'absolute',
					content: '""',
					width: '34%',
					height: '2.6%',
					left: '50%',
					transform: 'translateX(-50%)',
					borderRadius: '50%',
					top: '14px',
					boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.5)',
					background: 'radial-gradient(rgba(0, 0, 0, 0.6), transparent 45%)',
				}}
			>
				<Box
					className="thread"
					pos="relative"
					w="7%"
					h="12%"
					top="-17px"
					left="50%"
					zIndex="99999"
					borderRadius="40% 40% 0 0"
					transform="translateX(-50%)"
					bgGradient="linear(to-b, #d6994a, #4b232c, #121212, #000000, #e8bb31 90%)"
				/>

				<Box
					className="blinking-glow"
					pos="absolute"
					w="100%"
					h="60%"
					left="50%"
					top="-55%"
					transform="translateX(-50%)"
					borderRadius="50%"
					background="#ff6000"
					filter="blur(68px)"
					animation="blinkGlow .13s infinite"
				/>

				<Box className="glow"
					pos="absolute"
					w="28%"
					h="20%"
					borderRadius="50% 50% 35% 35%"
					left="50%"
					top="-48px"
					transform="translateX(-50%)"
					bg="rgba(0, 133, 255, .5)"
					boxShadow="0 -40px 30px 0 #dc8a0c, 0 40px 50px 0 #dc8a0c, inset 3px 0 2px 0 rgba(0, 133, 255, .6), inset -3px 0 2px 0 rgba(0, 133, 255, .6)"
					_before={{
						width: '70%',
						height: '60%',
						left: '50%',
						transform: 'translateX(-50%)',
						bottom: 0,
						borderRadius: '50%',
						background: 'rgba(0, 0, 0, 0.21)'
					}}
				/>

				<Box
					className="flame"
					pos="relative"
					w="30%"
					h="40%"
					left="50%"
					transformOrigin="50% 100%"
					transform="translateX(-50%)"
					bottom="50%"
					borderRadius="50% 50% 20% 20%"
					bgGradient="linear(white 80%, transparent)"
					animation="moveFlame 5s linear infinite, enlargeFlame 6s linear infinite"
					_before={{
						width: '100%',
						height: '100%',
						borderRadius: '50% 50% 20% 20%',
						boxShadow: '0 0 15px 0 rgba(247, 93, 0, .4), 0 -6px 4px 0 rgba(247, 128, 0, .7)',
					}}
				/>
			</Box>
		</Box>
	)
}

export const Candles = () => {
	const circlesBreakpoint = useRecoilValue(circlesBreakpointState)

	const titleFontSize = getBreakpointValue({ base: 'lg', lg: 'xl', xl: '2xl', xxxl: '3xl' }, circlesBreakpoint)
	const titleBottom = getBreakpointValue({ base: '30%', sm: '25%', md: '22%' }, circlesBreakpoint)
	const containerBottom = getBreakpointValue({ base: '-44%', md: '-3%' }, circlesBreakpoint)

	return (
		<Flex
			pos="absolute"
			bottom={containerBottom}
			w="100%"
			h="45%"
		>
			<Candle transform="scale(0.6)" />

			<Candle transform="scale(0.55) translateX(112%) translateY(-9%)" opacity={0.9} />
			<Candle transform="scale(0.55) translateX(-112%) translateY(-9%)" opacity={0.9} />

			<Candle transform="scale(0.45) translateX(256%) translateY(-32%)" opacity={0.8} />
			<Candle transform="scale(0.45) translateX(-256%) translateY(-32%)" opacity={0.8} />

			<Candle transform="scale(0.4) translateX(402%) translateY(-50%)" opacity={0.7} />
			<Candle transform="scale(0.4) translateX(-402%) translateY(-50%)" opacity={0.7} />

			<Candle transform="scale(0.35) translateX(575%) translateY(-73%)" opacity={0.6} />
			<Candle transform="scale(0.35) translateX(-575%) translateY(-73%)" opacity={0.6} />

			<Candle transform="scale(0.3) translateX(786%) translateY(-104%)" opacity={0.5} />
			<Candle transform="scale(0.3) translateX(-786%) translateY(-104%)" opacity={0.5} />

			<Candle transform="scale(0.25) translateX(1055%) translateY(-148%)" opacity={0.4} />
			<Candle transform="scale(0.25) translateX(-1055%) translateY(-148%)" opacity={0.4} />

			<Candle transform="scale(0.2) translateX(1440%) translateY(-212%)" opacity={0.3} />
			<Candle transform="scale(0.2) translateX(-1440%) translateY(-212%)" opacity={0.3} />

			<Candle transform="scale(0.15) translateX(2033%) translateY(-318%)" opacity={0.2} />
			<Candle transform="scale(0.15) translateX(-2033%) translateY(-318%)" opacity={0.2} />

			<Text
				zIndex={1334}
				fontSize={titleFontSize}
				fontWeight="bold"
				align="center"
				pos="absolute"
				bottom={titleBottom}
				left={0}
				right={0}
			>
				17 YEARS! 🥳🎉
			</Text>
		</Flex>
	)
}
